import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useFormData from '@/hooks/useFormData';
import { RootState } from '@/lib/userType';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Loader2, User } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { userLogin, setUser } from '@/store/user/userSlice.js';
import { useSendOtpMutation, useVerifyOtpMutation } from '@/store/adminAuthApi';

export default function AuthForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sendOtpFn, { isLoading: isSendingOTP }] = useSendOtpMutation();
  const [verifyOtpFn, { isLoading: isVerifiying, error }] =
    useVerifyOtpMutation();
  const { formData, handleChange, resetForm } = useFormData({
    email: '',
    password: '',
    role: '',
  });
  const { isLoading } = useSelector((state: RootState) => state.user);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showOtpModal, setShowOtpModal] = React.useState(false);
  const [otp, setOtp] = React.useState('');
  const [registerError, setRegisterError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      if (formData.role === 'admin') {
        let deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
          deviceId = crypto.randomUUID();
          localStorage.setItem('deviceId', deviceId);
        }

        const user = await sendOtpFn({
          email: formData.email,
          password: formData.password,
          deviceId,
          role: 'admin',
        }).unwrap();

        if (!user.token) {
          setShowOtpModal(true);
        } else {
          dispatch(setUser(user));
          navigate('/');
        }
        return;
      }

      // For non-admin roles, handle login properly
      await dispatch(userLogin(formData)).unwrap(); // Ensure this is awaited
      navigate('/');
    } catch (error) {
      console.error('Error:', error);
      setRegisterError(error || 'Something went wrong');
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem('deviceId', deviceId);
    }

    try {
      const response = await verifyOtpFn({
        email: formData.email,
        otp,
        deviceId,
      }).unwrap();

      if (response.success) {
        // Assuming API returns { success: true } on valid OTP
        await dispatch(userLogin(formData)).unwrap();
        setShowOtpModal(false);
        navigate('/');
        resetForm();
      } else {
        console.error('OTP verification failed');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
    }
  };

  return (
    <>
      <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-1">
          <label className="text-sm font-medium">
            Email <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <Input
              id="email"
              type="email"
              onChange={handleChange}
              placeholder="Enter your email"
              className="pl-3 pr-10 placeholder:text-foreground"
              required
            />
            <User className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-foreground" />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">
            Password <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              onChange={handleChange}
              placeholder="Enter your password "
              className="pl-3 pr-10 placeholder:text-foreground"
              required
            />
            {showPassword ? (
              <EyeOff
                onClick={() => setShowPassword(false)}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-foreground cursor-pointer"
              />
            ) : (
              <Eye
                onClick={() => setShowPassword(true)}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-foreground cursor-pointer"
              />
            )}
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Select User Type</label>
          <Select
            onValueChange={(value) =>
              handleChange({
                target: { id: 'role', value },
              } as React.ChangeEvent<HTMLInputElement>)
            }
            required
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select User Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Administrator</SelectItem>
              <SelectItem value="hr">HR</SelectItem>
              <SelectItem value="Instructor">Instructor</SelectItem>
              <SelectItem value="student">Student</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {registerError && (
          <p className="text-sm text-destructive">
            {formData.role === 'admin'
              ? registerError?.data?.message
              : typeof registerError === 'object'
              ? ''
              : registerError}
          </p>
        )}

        <div className="text-right">
          <button
            onClick={() => navigate('/forgot-password')}
            className="text-sm text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
          >
            Forgot Password?
          </button>
        </div>
        <Button
          className="w-full bg-foreground text-background font-bold"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ||
            (isSendingOTP && (
              <Loader2 className="text-primary-foreground animate-spin" />
            ))}{' '}
          Login
        </Button>
      </form>

      <Dialog
        open={showOtpModal}
        onOpenChange={setShowOtpModal}
        className="text-foreground"
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <p className="text-foreground">
                Administrator Verification Required
              </p>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                Enter OTP <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter verification code"
                required
                maxLength={6}
                className="pl-3 pr-10 placeholder:text-foreground"
              />
              <p className="text-sm text-muted-foreground">
                {error
                  ? error?.data?.message || 'OTP verification failed'
                  : ' A verification code has been sent to your registered email'}
              </p>
            </div>
            <Button
              type="submit"
              className="w-full bg-foreground text-background font-bold"
              disabled={isLoading}
            >
              {isLoading && (
                <Loader2 className="text-primary-foreground animate-spin mr-2" />
              )}
              Verify & Login
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* {showVideo && <UserLoginVideo />} */}
    </>
  );
}
